/**
 * The general procedure-consent workflow primitive (`BACKLOG.md` M9's own header note: M9 and a
 * future dermatology consent workflow "share... the general procedure-consent workflow engine").
 * Built fresh here — `BACKLOG.md` M3 built no consent workflow of any kind, so there is nothing to
 * extend, only a first, deliberately reusable version to build.
 *
 * **Three distinct consent concepts, not one generic "procedure consent"** — the specific finding
 * this module exists to make representable, per M9's own brief:
 *
 *   1. `photograph` — consent to *take* clinical photographs, genuinely separate from consenting to
 *      the treatment itself. A patient can consent to a filler injection while declining to be
 *      photographed, or the reverse (agreeing to before/after photos as part of a consultation with
 *      no treatment yet performed).
 *   2. `marketing_social_media` — consent to *use* an already-taken photograph for marketing or
 *      social media, separately trackable and separately revocable from the photograph consent
 *      itself. A patient who consents to clinical photo documentation has said nothing about whether
 *      the practice may post that photo to Instagram; collapsing the two into one flag is the
 *      specific modeling mistake this split exists to prevent.
 *   3. Per-procedure/per-product-class treatment consents — `treatment_general` (a reusable generic
 *      slot a future module can use for its own procedure consent) plus, for this module's own
 *      injectable charting, four genuinely separate cosmetic-product consents
 *      (`aesthetics/injectables.ts`'s `requiredConsentTypeForProductClass`) confirmed via AmSpa's
 *      Injectable Toolkit as real, distinct documents in practice — filler, Radiesse, and Sculptra
 *      are chemically and procedurally different from a neuromodulator and from each other, and a
 *      single generic "cosmetic procedure consent" would misrepresent what a patient actually agreed
 *      to if a complication or a dispute is ever litigated.
 *
 * `non_refund_acknowledgment` is the one type here that is not a treatment consent at all — a
 * separate business acknowledgment (no refund for a completed cosmetic service), tracked through the
 * same acceptance-event shape because the evidentiary need (who agreed, to exactly what version, and
 * when) is identical, even though its subject matter is commercial rather than clinical.
 *
 * **Revocability is a property of the type, not of every row.** Only `marketing_social_media`
 * consent is expected to be revoked in the ordinary course of practice (a patient changes their mind
 * about a photo already posted); `isRevocableConsentType` names that explicitly rather than leaving
 * every route to decide for itself whether "revoke" is a sensible action for a given row. The other
 * types are not *structurally* prevented from carrying a `revokedAt` — `server/src/db/schema/
 * aesthetics.ts`'s `aestheticsConsentDocuments` table carries the column on every row, the same
 * "don't build two mechanisms for one concept" reasoning `legal.ts`'s own acceptance table uses — but
 * a route only exposes the revoke action where `isRevocableConsentType` says it makes sense.
 */

export const CONSENT_DOCUMENT_TYPES = [
  'photograph',
  'marketing_social_media',
  'treatment_general',
  'cosmetic_filler',
  'cosmetic_radiesse',
  'cosmetic_sculptra',
  'cosmetic_neuromodulator',
  'non_refund_acknowledgment',
] as const;
export type ConsentDocumentType = (typeof CONSENT_DOCUMENT_TYPES)[number];

/** How the patient's agreement was actually captured — a signed paper form scanned in, a tablet
 * signature at check-in, or a portal click. Open-but-checked at the application boundary, matching
 * `dermatology_patch_test_readings.reading`'s own convention: the database column is plain text. */
export const CONSENT_SIGNING_METHODS = ['in_person', 'electronic'] as const;
export type ConsentSigningMethod = (typeof CONSENT_SIGNING_METHODS)[number];

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isConsentDocumentType(value: string): value is ConsentDocumentType {
  return includes(CONSENT_DOCUMENT_TYPES, value);
}

export function isConsentSigningMethod(value: string): value is ConsentSigningMethod {
  return includes(CONSENT_SIGNING_METHODS, value);
}

/** See this file's header — only marketing/social-media-use consent is treated as ordinarily
 * revocable by a route. */
export function isRevocableConsentType(type: ConsentDocumentType): boolean {
  return type === 'marketing_social_media';
}

const CONSENT_DOCUMENT_LABELS: Record<ConsentDocumentType, string> = {
  photograph: 'Consent to photograph',
  marketing_social_media: 'Consent to marketing / social media use',
  treatment_general: 'General treatment consent',
  cosmetic_filler: 'Dermal filler consent',
  cosmetic_radiesse: 'Radiesse consent',
  cosmetic_sculptra: 'Sculptra consent',
  cosmetic_neuromodulator: 'Neuromodulator (Botox/Dysport/Xeomin/Jeuveau) consent',
  non_refund_acknowledgment: 'Non-refund acknowledgment',
};

/** A human-readable label for a consent type, for a list/summary view or a select control. Falls
 * back to the raw value for anything unrecognised rather than throwing — a route rendering a value
 * this build does not recognise (corrupt data, a half-finished migration) should show something
 * rather than crash the page, matching `dermatology/recallService.ts`'s own `labelOf` reasoning for
 * an unrecognised confidentiality reason. */
export function consentDocumentTypeLabel(type: string): string {
  return isConsentDocumentType(type) ? CONSENT_DOCUMENT_LABELS[type] : type;
}

/** Is `consent` (a real, already-fetched row) currently in force: not revoked, and belonging to the
 * expected type? Pure so both the server (deciding whether an injectable/photo write may proceed)
 * and the web app (deciding what to show as "on file") share the identical answer. `revokedAt` is
 * `string | null` rather than `Date | null` throughout this module, matching `columns.ts`'s own
 * `instant` reasoning: read as a string, never converted, so no process-local timezone can leak in. */
export function isConsentInForce(
  consent: { readonly consentType: string; readonly revokedAt: string | null },
  expectedType: ConsentDocumentType,
): boolean {
  return consent.consentType === expectedType && consent.revokedAt === null;
}
